import type { paths } from "@lightmill/log-api"
import "./html-loading-button-element"
import createClient from "openapi-fetch"
import commonStyleUrl from "../common.css?url"
import { getElement } from "../lib/get-elements"
import { type ObservedAttribute, html } from "../lib/utils"
import { WithAttributeProps, string } from "../mixins/with-attributes-props"
import { HTMLLoadingButtonElement } from "./html-loading-button-element"

const template = html`
	<style>
		@import url(${commonStyleUrl});
		form > div {
			margin: var(--space-m) 0;
		}
    form .loader {
      display: none;
    }
		form.is-loading .loader {
      display: inline-block;
    }
    form.is-loading .content {
      display: none;
    }
    form .error {
			display: none;
      color: var(--red-300);
    }
		form.has-error .error {
			display: block;
		}
		form .controls {
			text-align: center;
		}
	</style>
	<form>
		<div class="error"></div>
		<label for="username" hidden>User:</label>
		<input
			type="text"
			name="username"
			autocomplete="username"
			readonly
			hidden
		/>
		<div>
			<label for="password">Password:</label>
			<input
				type="password"
				id="password"
				name="password"
				autocomplete="current-password"
				required
			/>
		</div>
		<div class="controls">
			<button type="submit" hidden></button>
			<x-loading-button icon="tabler:login">
				<iconify-icon icon="tabler:login" inline flip="horizontal" slot="icon"></iconify-icon>	
				<span slot>Login</span>
			</x-loading-button>
		</div>
	</form>
`

export class HTMLLoginFormElement extends WithAttributeProps(HTMLElement, {
	apiSrc: string(),
	username: string({ default: "host" }),
}) {
	#formElement
	#errorElement
	#usernameElement
	#loginButtonElement

	#isLoading = false
	#hasError = false

	constructor() {
		super()
		let shadowRoot = this.attachShadow({ mode: "closed" })
		shadowRoot.appendChild(template.content.cloneNode(true))
		this.#formElement = getElement("form", shadowRoot, HTMLFormElement)
		this.#errorElement = getElement(".error", shadowRoot, HTMLDivElement)
		this.#usernameElement = getElement(
			"input[name=username]",
			this.#formElement,
			HTMLInputElement,
		)
		this.#loginButtonElement = getElement(
			"x-loading-button",
			this.#formElement,
			HTMLLoadingButtonElement,
		)
	}

	get isLoading() {
		return this.#isLoading
	}

	get hasError() {
		return this.#hasError
	}

	#setIsLoading(isLoading: boolean) {
		this.#isLoading = isLoading
		if (isLoading) {
			this.#formElement.classList.add("is-loading")
			this.#loginButtonElement.loading = true
			this.#disableAll()
		} else {
			this.#formElement.classList.remove("is-loading")
			this.#loginButtonElement.loading = false
			this.#enableAll()
		}
	}

	#disableAll() {
		for (let elt of this.#formElement.querySelectorAll(
			"button, input, x-loading-button",
		)) {
			elt.setAttribute("disabled", "true")
		}
	}
	#enableAll() {
		for (let elt of this.#formElement.querySelectorAll(
			"button, input, x-loading-button",
		)) {
			elt.removeAttribute("disabled")
		}
	}

	#setError(message: string | null) {
		this.#hasError = message != null && message !== ""
		if (this.#hasError) {
			this.#formElement.classList.add("has-error")
			this.#errorElement.textContent = message
		} else {
			this.#formElement.classList.remove("has-error")
			this.#errorElement.textContent = ""
		}
	}

	connectedCallback() {
		this.#formElement.addEventListener("submit", this.#handleSubmit)
		this.#usernameElement.value = this.username
		this.#loginButtonElement.addEventListener("click", () => {
			this.#formElement.requestSubmit()
		})
	}

	disconnectedCallback() {
		this.#formElement.removeEventListener("submit", this.#handleSubmit)
	}

	static observedAttributes = ["username"] as const

	attributeChangedCallback(
		name: ObservedAttribute<typeof HTMLLoginFormElement>,
		_oldValue: string,
		newValue: string,
	) {
		if (name !== "username") return
		this.#usernameElement.value = newValue
	}

	#handleSubmit = (event: Event) => {
		event.preventDefault()
		this.#setError(null)
		const formData = new FormData(this.#formElement)
		const password = formData.get("password")
		const username = formData.get("username")
		if (typeof password !== "string") {
			// This should never happen because the input is required.
			this.#setError("Please enter a password")
			return
		}
		if (typeof username !== "string") {
			// This should never happen because the input is already filled with the
			// username.
			this.#setError("Unexpected Error: username is not set")
			return
		}
		this.#setIsLoading(true)
		this.#login({ username, password })
			.then(() => {
				this.dispatchEvent(new LoginFormLoginEvent(username))
			})
			.catch((error) => {
				if (
					error instanceof LoadingError &&
					error.code === "INVALID_CREDENTIALS"
				) {
					this.#setError("Invalid credentials")
				} else if (
					error instanceof LoadingError &&
					error.code === "SESSION_EXISTS"
				) {
					this.#setError("User already logged in")
					return
				} else if (error instanceof Error) {
					this.#setError(`Unexpected error: ${error.message}`)
					// biome-ignore lint/suspicious/noConsole: this is on purpose
					console.error(error)
				} else {
					this.#setError("Unexpected error")
					// biome-ignore lint/suspicious/noConsole: this is on purpose
					console.error(error)
				}
				this.dispatchEvent(new LoginFormErrorEvent(error))
			})
			.finally(() => {
				this.#formElement.reset()
				this.#usernameElement.value = this.username
				this.#setIsLoading(false)
			})
	}

	reset() {
		if (this.isLoading) throw new Error("Cannot reset while loading")
	}

	async #login({ password, username }: { password: string; username: string }) {
		if (this.apiSrc == null) {
			throw new Error("apiSrc is not set")
		}
		let client = createClient<paths>({ baseUrl: this.apiSrc })
		const response = await client.POST("/sessions", {
			body: {
				data: { type: "sessions", attributes: { role: "host" } },
			},
			headers: {
				"Content-Type": "application/vnd.api+json",
				Authorization: `Basic ${btoa(`${username}:${password}`)}`,
			},
			credentials: "include",
		})
		if (response.error) {
			let firstError = response.error.errors[0]
			throw new LoadingError(firstError.code, firstError.detail)
		}
		return response
	}
}

class LoadingError extends Error {
	#code: "INVALID_CREDENTIALS" | "SESSION_EXISTS"

	get code() {
		return this.#code
	}

	get name() {
		return "LoadingError"
	}

	constructor(
		code: "INVALID_CREDENTIALS" | "SESSION_EXISTS",
		message?: string,
	) {
		super(message)
		this.#code = code
	}
}

export class LoginFormLoginEvent extends CustomEvent<{ username: string }> {
	constructor(username: string) {
		super("login", { detail: { username } })
	}
}

export class LoginFormErrorEvent extends CustomEvent<{ error: unknown }> {
	constructor(error: unknown) {
		super("login-error", { detail: { error } })
	}
}

customElements.define("x-login-form", HTMLLoginFormElement)
